import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon } from "lucide-react";

export interface DateInputProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  className?: string;
  style?: React.CSSProperties;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  isClearable?: boolean;
  disabled?: boolean;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  calenderPosition?: string;
  id?: string;
  rest?: any;
  bgColor?: string;
  bgInputColor?: string;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  className = "",
  style,
  maxDate,
  minDate,
  placeholder = "MM/DD/YYYY",
  isClearable = true,
  disabled = false,
  inputClassName = "!w-full !h-full px-3 py-2 rounded-md",
  inputStyle,
  calenderPosition = "20%",
  id,
  bgColor = "bg-white dark:!bg-[#040d19]",
  bgInputColor = "bg-gray-400",
  ...rest
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const portalId = "app-datepicker-portal";
  const inputRef = useRef<HTMLInputElement>(null);
  const calenderRef = useRef<DatePicker>(null);

  const formatDateForInput = useMemo(
    () =>
      (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      },
    [],
  );

  // Sync internal state when external value changes
  useEffect(() => {
    // Only set selectedDate if value is a valid Date object
    const isValidDate = value instanceof Date && !Number.isNaN(value.getTime());
    setSelectedDate(isValidDate ? value : null);

    // Only format date for input if it's a valid Date object
    if (isValidDate) {
      setInputValue(formatDateForInput(value));
    } else {
      setInputValue("");
    }
  }, [value, formatDateForInput]);

  // Ensure a body-level portal exists so the calendar is never clipped by table overflow
  useEffect(() => {
    let portal = document.getElementById(portalId);
    if (!portal) {
      portal = document.createElement("div");
      portal.setAttribute("id", portalId);
      document.body.appendChild(portal);
    }
  }, []);

  const isCompleteDateFormat = (text: string): boolean => {
    const cleaned = text.replace(/[^\d/]/g, "");
    const parts = cleaned.split("/").filter((part) => part.length > 0);
    if (parts.length !== 3) return false;
    const [mm, dd, yyyy] = parts;
    return mm.length === 2 && dd.length === 2 && yyyy.length === 4;
  };

  const tryParseDate = (text: string): Date | null => {
    if (!isCompleteDateFormat(text)) return null;
    const [mm, dd, yyyy] = text.split("/");
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const year = parseInt(yyyy, 10);
    if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) return null;
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
      return d;
    }
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue(next);

    // Only propagate when complete and valid; otherwise do nothing (prevents API trigger)
    const parsed = tryParseDate(next);
    if (parsed) {
      setSelectedDate(parsed);
      onChange(parsed);
      // Don't close picker when typing - let user continue or select from calendar
    }
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      setInputValue(formatDateForInput(date));
      // Only call API when date is selected from calendar
      onChange(date);
    } else {
      setInputValue("");
      onChange(null);
    }
  };

  const handleBlur = () => {
    // For single date selector, only clear if the input value is not a valid complete date
    // This prevents clearing when user has selected a valid date from calendar
    if (inputValue && !isCompleteDateFormat(inputValue)) {
      setSelectedDate(null);
      setInputValue("");
      onChange(null);
    }
  };

  const wrapperStyle = inputStyle ? { height: inputStyle.height } : {};
  const finalInputClassName = inputStyle?.height ? `!w-full px-3 rounded-md` : inputClassName;

  return (
    <div className={`relative ${className} w-full`} style={style}>
      <div className="w-full border-gray rounded-md" style={wrapperStyle}>
        <DatePicker
          selected={selectedDate ?? null}
          onChange={handleDateChange}
          onClickOutside={handleBlur}
          maxDate={maxDate}
          minDate={minDate}
          dropdownMode="select"
          showYearDropdown
          showMonthDropdown
          showIcon
          ref={calenderRef}
          showPopperArrow={false}
          disabled={disabled}
          wrapperClassName={`w-full ${bgInputColor} rounded-md`}
          calendarClassName="w-full"
          clearButtonClassName="!right-[10%] dark:!text-black"
          icon={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <CalendarIcon
              className="right-0"
              onClick={() => {
                calenderRef.current?.setOpen(true);
              }}
              style={{ top: calenderPosition }}
            />
          }
          className={`w-full ${bgColor}
           text-black dark:text-white rounded-md`}
          placeholderText={placeholder}
          popperClassName="react-datepicker-popper !z-[2147483647]"
          portalId={portalId}
          customInput={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <input
              id={id}
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder={placeholder}
              disabled={disabled}
              className={`${finalInputClassName} rounded-md`}
              style={inputStyle}
            />
          }
          isClearable={isClearable ? Boolean(selectedDate || inputValue) : false}
          dateFormat="MM/dd/yyyy"
          {...rest}
        />
      </div>
    </div>
  );
};
export default memo(DateInput);
